import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from 'react';
import '../App.css';
import ChatForm from '../components/ChatForm';
import ChatMessage from './ChatMessage';
import ChatHeader from './ChatHeader';
import WelcomeMessage from './WelcomeMessage';
import { ApiService, CheckCompleteResult } from '../services/ApiService';
import { v4 as uuidv4 } from 'uuid';
import {
  processSQLQuery,
  processSQLResult,
} from '../ApiUtil';
import LoadingMessage from './LoadingMessage';
import { ChatMCPProps } from '../interfaces/chatMCPProps';
import { SemanticModel } from '../services/dto/semanticModel';
import { ACCOUNT_MAPPING, getAccountNameFromId } from '../utils/accountMapping';
import { Snackbar } from '@cimpress/react-components';
import { MessageBuilder } from '../utils/MessageBuilder';
import { ChatMCPAuthProvider } from '../contexts/ChatMCPAuthContext';

const check_complete = async (
  accessToken: string | undefined,
  startTime: number,
  prompt: string,
  fileName: string,
  prompt_id: string,
  accountName: string
) => {
  try {
    const result: CheckCompleteResult = await ApiService.checkComplete(
      accessToken,
      prompt,
      fileName,
      prompt_id,
      accountName
    );

    const endComplete = performance.now();
    const loadingTimeToComplete = Math.round((endComplete - startTime) / 1000);

    return {
      shouldContinue: result.shouldContinue,
      message: result.message,
      loadingTimeToComplete,
    };
  } catch (error) {
    console.error('Error in checkComplete:', error);
    return { shouldContinue: false, message: null, loadingTimeToComplete: 0 };
  }
};

// Helper function to handle form submission and API call
const handleSubmit = async (
  prompt: string,
  fileName: any,
  accessToken: string | undefined,
  addMessage: (msg: any) => void,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setColumns: React.Dispatch<React.SetStateAction<any>>,
  setData: React.Dispatch<React.SetStateAction<any>>,
  isCompleteEnabled: boolean,
  setIsCompleteEnabled: React.Dispatch<React.SetStateAction<any>>,
  isOnlySQLEnabled: boolean,
  setSqlQuery: React.Dispatch<React.SetStateAction<any>>,
  filters: string,
  setChatHistory: React.Dispatch<React.SetStateAction<any[]>>,
  setLoadingMessage: React.Dispatch<React.SetStateAction<string>>,
  setErrorObj: React.Dispatch<React.SetStateAction<any>>,
  accountName: string
) => {
  const promptId = uuidv4();
  if (!accessToken) {
    setErrorObj({
      title: 'Access token is missing',
      detail: 'Access token is missing',
      error_type: 'AuthenticationError',
      trace_id: 'N/A',
    });
    return;
  }
  setIsLoading(true);
  const startTime = performance.now();

  addMessage(MessageBuilder.user(promptId, fileName, accountName, prompt));
  // prompt = sanitizeString(prompt) + " "+ filters;
  const partialResult = ApiService.submitPrompt(
    accessToken,
    prompt,
    fileName,
    isCompleteEnabled,
    promptId,
    isOnlySQLEnabled
  );
  try {
    if (isCompleteEnabled) {
      setLoadingMessage('Trying to fetch insights...');
      const completeResult = await check_complete(
        accessToken,
        startTime,
        prompt,
        fileName,
        promptId,
        accountName
      );

      if (completeResult.message) {
        const formattedElement = (
          <div>
            {completeResult.message.split('\n').map((line, idx) => (
              <p key={idx}>{line}</p>
            ))}
            <p>({completeResult.loadingTimeToComplete}s)</p>
          </div>
        );
        addMessage(MessageBuilder.complete(promptId, fileName, accountName, formattedElement));
      }

      if (!completeResult.shouldContinue) {
        setIsLoading(false);
        return;
      }
    }

    try {
      setLoadingMessage('Generating SQL query...');
      // const partialResult = await ApiService.submitPrompt(accessToken, prompt, fileName, isCompleteEnabled, promptId, isOnlySQLEnabled);

      const { message, request_id } = await partialResult;
      const botMessage = message.text.replace(/__/g, '');
      const tempSqlQuery = message.sql_statement;
      const endTime = performance.now();
      const loadingTime = Math.round((endTime - startTime) / 1000);

      const botMessageWithTime = `${botMessage} (${loadingTime}s)`;
      if (botMessage) {
        addMessage(MessageBuilder.bot(promptId, fileName, accountName, botMessageWithTime, false));
      }

      // Suggestions
      if (message.suggestions && message.suggestions.length > 0) {
        // Filter out only those items that are of type 'suggestions'
        const suggestions = message.suggestions;

        // Add message
        addMessage(MessageBuilder.suggestions(promptId, fileName, accountName, suggestions));
        return;
      }

      if (isOnlySQLEnabled) {
        processSQLQuery(tempSqlQuery, addMessage, promptId, fileName, accountName);
        setIsLoading(false);
        return;
      }

      if (tempSqlQuery && tempSqlQuery.trim && tempSqlQuery.trim().length > 0) {
        addMessage(MessageBuilder.tmpQuery(promptId, fileName, accountName, tempSqlQuery));

        setLoadingMessage('Fetching the data from warehouse...');
        const { sql_result, metadata } = await ApiService.executeCompletion(
          request_id,
          accessToken,
          fileName
        );

        setChatHistory((prevHistory) => {
          if (!prevHistory || !Array.isArray(prevHistory)) return [];
          return prevHistory.filter(
            (msg, index) =>
              !(msg.type === 'tmp_query' && index === prevHistory.length - 1)
          );
        });

        processSQLResult(sql_result, tempSqlQuery, setColumns, addMessage, {
          prompt_id: promptId,
          semantic_model: fileName,
          account_name: accountName,
        }, metadata);
      }
    } catch (error: any) {
      console.error('Error in handleSubmit:', error);
      setErrorObj(error);
    } finally {
      setIsLoading(false);
      setLoadingMessage('...awaiting response');
    }
  } catch (error: any) {
    console.error('Error in handleSubmit:', error);
    setErrorObj(error);
  }
};

const executeSQLStatement = async (
  accessToken: string | undefined,
  sql_query: string,
  setColumns: React.Dispatch<React.SetStateAction<any>>,
  addMessage: (msg: any) => void,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  promptId: string,
  accountId: string | undefined,
  fileName: string,
  setErrorObj: React.Dispatch<React.SetStateAction<any>>
) => {
  setIsLoading(true);
  const accountName = getAccountNameFromId(accountId);
  try {
    const { sql_result, metadata } = await ApiService.executeSQL(
      accessToken,
      sql_query,
      accountName,
      fileName
    );
    processSQLResult(sql_result, sql_query, setColumns, addMessage, {
      prompt_id: promptId,
      semantic_model: fileName,
      account_name: accountName,
    }, metadata); // Now includes metadata for proper table formatting
  } catch (error) {
    setErrorObj(error);
  }
  setIsLoading(false);
};

const getUserName = (email: string) => {
  if (!email) return 'there';

  // Current implementation
  const namePart = email.split('@')[0];
  const firstName = namePart.split('.')[0];
  return firstName.charAt(0).toUpperCase() + firstName.slice(1);
};

// Main application component
const ChatMCPComponent: React.FC<ChatMCPProps> = (props) => {
  const [prompt, setPrompt] = useState('');
  const [fileName, setFileName] = useState<string>('');
  const [fileOptions, setFileOptions] = useState<SemanticModel[] | null>(null); // Start as null (loading)
  const accessToken = props.authContext.accessToken;
  const authContext = props.authContext;
  const moduleName = props.moduleName;
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [columns, setColumns] = useState<any[]>([]);
  const [data, setData] = useState<any[]>([]);
  const [hasStartedChat, setHasStartedChat] = useState<boolean>(false);
  const [chatHistory, setChatHistory] = useState<any[]>([]);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const [loadingTime, setLoadingTime] = useState<number>(0);
  const [isCompleteEnabled, setIsCompleteEnabled] = useState<boolean>(false);
  const [isOnySQLEnabled, setIsOnySQLEnabled] = useState<boolean>(false);
  const [sqlQuery, setSqlQuery] = useState('');
  const lastMessageRef = useRef<HTMLDivElement | null>(null);
  const [filters, setFilter] = useState('');
  const [loadingMessage, setLoadingMessage] = useState<string>(
    '...awaiting response'
  );
  const [showWelcomeMessage, setShowWelcomeMessage] = useState<boolean>(true);
  const [errorObj, setErrorObj] = useState<any>(null);

  const searchParams = new URLSearchParams(window.location.search);
  const [promptId, setPromptId] = useState(searchParams.get('promptId'));

  // Get account ID from auth context
  const accountId = authContext.profile?.['https://claims.cimpress.io/account'];
  const isAccountAllowed =
    accountId && Object.hasOwn(ACCOUNT_MAPPING, accountId);
  const accountName = getAccountNameFromId(accountId);

  useEffect(() => {
    if (promptId) {
      // Remove 'promptId' from the query parameters
      searchParams.delete('promptId');

      // Construct the new URL without promptId
      const newSearch = searchParams.toString();
      const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;

      // Update the browser history
      console.log('newURL:', newUrl);
      window.history.replaceState(null, '', newUrl);

      // Clear the promptId state after updating URL
      setPromptId(null);
    }
  }, [promptId]);

  // Auto-dismiss error after 4 seconds
  useEffect(() => {
    if (errorObj) {
      const timer = setTimeout(() => setErrorObj(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [errorObj]);

  const addMessage = (message: any) => {
    setChatHistory((prevHistory) => [
      ...prevHistory,
      { ...message, id: uuidv4(), ref: lastMessageRef, sqlQuery: sqlQuery }, // Add a unique ID to each message
    ]);
  };

  // Combined effect for initial mount: fetch file names and set welcome message
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!accessToken) return;
      try {
        console.log('Fetching semantic models for account1:', accountName);
        const semanticModels: SemanticModel[] = await ApiService.fetchFileNames(
          accessToken,
          accountName
        );
        setFileOptions(semanticModels); // This could be empty array or populated array
      } catch (error) {
        console.error('Error fetching file names:', error);
        setFileOptions([]); // Set empty array on error
      }
      // Set welcome message to true on initial mount
      setShowWelcomeMessage(true);
    };
    fetchInitialData();
  }, [accessToken]);

  // Effect for promptId: URL cleanup and data fetch
  useEffect(() => {
    if (promptId) {
      // Remove 'promptId' from the query parameters
      searchParams.delete('promptId');
      // Construct the new URL without promptId
      const newSearch = searchParams.toString();
      const newUrl = `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`;
      // Update the browser history
      console.log('newURL:', newUrl);
      window.history.replaceState(null, '', newUrl);
      // Clear the promptId state after updating URL
      setPromptId(null);
      // Fetch data by promptId
      const fetchData = async () => {
        if (accessToken) {
          try {
            const result = await ApiService.getByPromptId(
              accessToken,
              promptId
            );
            if (!result || result.length === 0) {
              return;
            }
            console.log('result by prompt id', result[0]?.PROMPT);
            if (result && result.length === 1) {
              const { PROMPT, SEMANTIC_FILE, SQL_QUERY } = result[0];
              setSqlQuery(SQL_QUERY);
              addMessage({
                type: 'user',
                text: PROMPT,
                prompt_id: promptId,
                semantic_model: SEMANTIC_FILE.replace('.yaml', ''),  // Add semantic model for feedback
                account_name: accountName  // Add account name for feedback
              });
              setFileName(SEMANTIC_FILE.replace('.yaml', ''));
              processSQLQuery(SQL_QUERY, addMessage, promptId, SEMANTIC_FILE.replace('.yaml', ''), accountName);
            }
          } catch (error) {
            console.error('Error fetching data by promptId:', error);
          }
        }
      };
      fetchData();
    }
  }, [promptId, accessToken]);

  // Effect for fileName: hide welcome message and use prompts from file metadata
  useEffect(() => {
    if (fileName) {
      setShowWelcomeMessage(false);
      // Remove all pre_chat messages before adding new ones
      setChatHistory((prevHistory) =>
        prevHistory.filter((msg) => msg.type !== 'pre_chat')
      );

      // Find the selected file and use its prompts as pre-chat messages
      const selectedFile = fileOptions?.find((file) => file.id === fileName);
      if (
        selectedFile &&
        selectedFile.prompts &&
        selectedFile.prompts.length > 0
      ) {
        selectedFile.prompts.forEach((prompt: string) => {
          const message = {
            type: 'pre_chat',
            text: prompt,
            id: uuidv4(),
          };
          addMessage(message);
        });
      }
    }
  }, [fileName, fileOptions]);

  // Update handleSubmit to set error message on API error
  const handleSubmitWrapper = useCallback(
    (
      event: React.FormEvent<HTMLFormElement> | null = null,
      promptData: string = ''
    ) => {
      if (event) {
        event.preventDefault();
      }
      setHasStartedChat(true);
      handleSubmit(
        promptData || prompt,
        fileName,
        accessToken,
        addMessage,
        setIsLoading,
        setColumns,
        setData, // Pass the state values to handleSubmit
        isCompleteEnabled,
        setIsCompleteEnabled,
        isOnySQLEnabled,
        setSqlQuery,
        filters,
        setChatHistory,
        setLoadingMessage,
        setErrorObj,
        accountName
      );
      setPrompt('');
    },
    [
      prompt,
      fileName,
      accessToken,
      filters,
      isCompleteEnabled,
      isOnySQLEnabled,
      accountName,
    ]
  );

  const executeSQLStatementWrapper = useCallback(
    (
      event: React.FormEvent<HTMLFormElement> | null = null,
      promptIddata = '',
      promptData: string = '',
      fileNameArg: string = fileName
    ) => {
      if (event) {
        event.preventDefault();
      }
      executeSQLStatement(
        accessToken,
        promptData || sqlQuery,
        setColumns,
        addMessage,
        setIsLoading,
        promptIddata || promptId || '',
        accountId,
        fileNameArg,
        setErrorObj // Pass the error setter here!
      );
    },
    [
      accessToken,
      sqlQuery,
      promptId,
      accountId,
      fileName,
      setColumns,
      addMessage,
      setIsLoading,
      setErrorObj,
    ]
  );

  useEffect(() => {
    if (lastMessageRef.current) {
      lastMessageRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [chatHistory]);

  if (!isAccountAllowed) {
    return (
      <div
        style={{
          textAlign: 'center',
          padding: '20px',
          fontSize: '16px',
          fontWeight: 'bold',
          color: '#d9534f',
        }}
      >
        <p>🚫 Access Restricted</p>
        <p>Your account does not have access to this module.</p>
        <p>
          Please reach out to{' '}
          <a href="mailto:dune@cimpress.com">dune@cimpress.com</a> to
          request access.
        </p>
      </div>
    );
  }

  return (
    <ChatMCPAuthProvider authContext={authContext}>
      <div className="chat-mcp-wrapper">
        <div
          className="out-container"
          style={{ display: 'flex', height: '90vh' }}
        >
          <ChatHeader
            isCompleteEnabled={isCompleteEnabled}
            setIsCompleteEnabled={setIsCompleteEnabled}
            isOnySQLEnabled={isOnySQLEnabled}
            setIsOnySQLEnabled={setIsOnySQLEnabled}
            columns={columns}
            setFilter={setFilter}
          />

          <div className="chat-app">
            {/* Error Toast using Cimpress Snackbar */}
            <Snackbar
              status="danger"
              show={!!errorObj}
              delay={8000}
              onHideSnackbar={() => setErrorObj(null)}
            >
              {errorObj && (
                <>
                  <b>{errorObj.title}</b>
                  <br />
                  {errorObj.detail && (
                    <>
                      <span>
                        <b>Detail:</b> {errorObj.detail}
                      </span>
                      <br />
                    </>
                  )}
                  {errorObj.error_type && (
                    <>
                      <span>
                        <b>Error Type:</b> {errorObj.error_type}
                      </span>
                      <br />
                    </>
                  )}
                  {errorObj.correlation_id && (
                    <>
                      <span>
                        <b>Correlation ID:</b> {errorObj.correlation_id}
                      </span>
                      <br />
                    </>
                  )}
                  <span>
                    <b>Trace ID:</b> {errorObj.trace_id}
                  </span>
                </>
              )}
            </Snackbar>
            <div className="chat-container" ref={chatContainerRef}>
              {showWelcomeMessage && (
                <WelcomeMessage authContext={authContext} />
              )}
              {chatHistory &&
                Array.isArray(chatHistory) &&
                chatHistory.map((msg, index) => (
                  <div
                    key={msg.id}
                    ref={
                      index === chatHistory.length - 1 ? lastMessageRef : null
                    }
                  >
                    <ChatMessage
                      message={msg}
                      executeSQLStatement={executeSQLStatementWrapper}
                      setPromptId={setPromptId}
                      setSqlQuery={setSqlQuery}
                      handleSubmitWrapper={handleSubmitWrapper}
                    />
                  </div>
                ))}

              {isLoading && (
                <LoadingMessage
                  isLoading={isLoading}
                  message={loadingMessage}
                />
              )}
            </div>
            <ChatForm
              handleSubmitWrapper={handleSubmitWrapper}
              prompt={prompt}
              fileName={fileName}
              setModelId={setFileName}
              fileOptions={fileOptions}
              isLoading={isLoading}
              setHasStartedChat={setHasStartedChat}
            />
          </div>
        </div>
      </div>
    </ChatMCPAuthProvider>
  );
};

export default ChatMCPComponent;
